/**
 * "People on your site now". Polls YOM's realtime report (last 5 minutes).
 * Rendered natively (not `<yom-report report="realtime">`) because the embed
 * labels it in analyst words ("sessions", "events"); owners get plain ones.
 */
import { useQuery } from '@tanstack/react-query';
import { qk } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import type { EmbedTokenManager } from '@/lib/yom/tokenManager';
import { fetchYomReport, type RealtimeData } from '@/lib/yom/reportClient';

export function useRealtime(
  projectId: string,
  manager: EmbedTokenManager | null,
  intervalMs = 30_000
) {
  return useQuery({
    queryKey: qk.analyticsRealtime(projectId),
    queryFn: async ({ signal }) => {
      const b = await manager!.getBundle();
      return fetchYomReport<RealtimeData>(
        b.sources.site,
        'realtime',
        { minutes: 5 },
        (rejected) => manager!.getToken('site', { rejected }),
        signal
      );
    },
    enabled: Boolean(manager),
    refetchInterval: intervalMs,
    refetchIntervalInBackground: false,
  });
}

export function LiveDot({ active, className }: { active: boolean; className?: string }) {
  return (
    <span className={cn('relative inline-flex h-2.5 w-2.5', className)} aria-hidden>
      {active && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
      )}
      <span
        className={cn(
          'relative inline-flex h-2.5 w-2.5 rounded-full',
          active ? 'bg-emerald-500' : 'bg-muted-foreground/40'
        )}
      />
    </span>
  );
}

export function peopleNow(n: number): string {
  if (n === 0) return 'Nobody on your site right now';
  return n === 1 ? '1 person on your site now' : `${n.toLocaleString()} people on your site now`;
}

/** Compact pill for the page header. */
export function LiveNowPill({ data }: { data: RealtimeData | undefined }) {
  const n = data?.active_visitors ?? 0;
  return (
    <span
      className="text-muted-foreground inline-flex items-center gap-2 text-sm"
      aria-live="polite"
    >
      <LiveDot active={n > 0} />
      {data ? peopleNow(n) : 'Checking who is online…'}
    </span>
  );
}

/** Card for the dashboard grid: the count and what they're looking at. */
export function LiveNowCard({ data, isPending }: { data?: RealtimeData; isPending: boolean }) {
  const n = data?.active_visitors ?? 0;
  const pages = (data?.top_pages ?? []).filter((p) => p.path).slice(0, 4);
  return (
    <div className="bg-card flex h-full min-h-[336px] flex-col rounded-xl border p-5 shadow">
      <div className="flex items-center gap-2 text-base font-semibold">
        <LiveDot active={n > 0} />
        Right now
      </div>
      {isPending ? (
        <div className="bg-muted mt-5 h-12 w-16 animate-pulse rounded" />
      ) : (
        <div className="mt-4 text-5xl font-semibold tabular-nums tracking-tight">{n}</div>
      )}
      <p className="text-muted-foreground mt-1 text-sm">
        {n === 1 ? 'person on your site' : 'people on your site'} in the last 5 minutes
      </p>
      <div className="mt-6 flex-1">
        <div className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide">
          Looking at
        </div>
        {pages.length ? (
          <ul className="space-y-2 text-sm">
            {pages.map((p) => (
              <li key={p.path} className="flex items-center justify-between gap-3">
                <span className="truncate" title={p.path ?? undefined}>
                  {p.path}
                </span>
                <span className="text-muted-foreground tabular-nums">{p.sessions ?? 0}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">
            When someone opens a page, you&apos;ll see it here.
          </p>
        )}
      </div>
      <p className="text-muted-foreground mt-4 text-xs">Updates every 30 seconds</p>
    </div>
  );
}
